package app.footyguide.web.page

import app.footyguide.domain.model.Team

/**
 * Shared UI utility functions to avoid duplication across page files.
 * Pure functions only — no DOM, no side effects.
 */

private const val ISO_DATE_LENGTH = 10
private const val TIME_LENGTH = 5
private const val LOGO_FALLBACK_LENGTH = 3
private val LOCAL_TIME_OPTIONS = js("({ hour: '2-digit', minute: '2-digit', hour12: false })")
private val CHANNEL_DISPLAY_PRIORITY = mapOf(
    "globo" to 0,
    "sportv" to 1,
    "premiere" to 2,
)

// ── Display Names ──────────────────────────────────────────────

fun displayName(id: String, resolvedName: String? = null): String =
    resolvedName ?: id.replaceFirstChar { it.uppercase() }

fun competitionDisplayName(
    id: String,
    shortName: String? = null,
    resolvedName: String? = null,
): String =
    shortName?.takeIf { it.isNotBlank() }
        ?: resolvedName
        ?: displayName(id)

fun orderChannelsForDisplay(channelIds: List<String>): List<String> =
    channelIds
        .withIndex()
        .sortedWith(
            compareBy<IndexedValue<String>>(
                { CHANNEL_DISPLAY_PRIORITY[it.value] ?: Int.MAX_VALUE },
                { it.index },
            ),
        )
        .map { it.value }

fun resolveTeam(teams: Map<String, Team>, teamId: String): Team? {
    teams[teamId]?.let { return it }

    return teams.values.firstOrNull { team ->
        teamMatchesId(team, teamId)
    }
}

fun teamMatchesId(team: Team?, teamId: String): Boolean {
    if (team == null) return false

    val normalizedTeamId = normalizeTeamKey(teamId)
    return normalizeTeamKey(team.id) == normalizedTeamId ||
        team.slug?.let { normalizeTeamKey(it) == normalizedTeamId } == true ||
        normalizeTeamKey(team.name) == normalizedTeamId ||
        normalizeTeamKey(team.shortName) == normalizedTeamId ||
        team.nameByLocale.values.any { normalizeTeamKey(it) == normalizedTeamId }
}

fun normalizeTeamKey(value: String): String =
    value.lowercase()
        .replace("-", "")
        .replace(" ", "")

// ── Date / Time ────────────────────────────────────────────────

fun extractKickoffTime(kickoff: String): String =
    formatBrowserTime(kickoff)

fun extractIsoDate(kickoff: String): String =
    kickoff.take(ISO_DATE_LENGTH)

fun formatRelativeDate(kickoff: String): String {
    val kickoffDate = kotlin.js.Date(kickoff)
    if (kickoffDate.toString() == "Invalid Date") return kickoff

    val today = kotlin.js.Date().toLocaleDateString("sv-SE") as String
    val tomorrow = kotlin.js.Date(kotlin.js.Date().getTime() + 24 * 60 * 60 * 1000)
        .toLocaleDateString("sv-SE") as String
    val date = kickoffDate.toLocaleDateString("sv-SE") as String
    val time = formatBrowserTime(kickoff)
    val label = when (date) {
        today -> "Hoje"
        tomorrow -> "Amanhã"
        else -> {
            val parts = date.split("-")
            "${parts[2]}/${parts[1]}"
        }
    }
    return "$label às $time"
}

private fun formatBrowserTime(kickoff: String): String {
    val kickoffDate = kotlin.js.Date(kickoff)
    if (kickoffDate.toString() == "Invalid Date") return kickoff.substringAfter("T").take(TIME_LENGTH)
    return kickoffDate.toLocaleTimeString("pt-BR", LOCAL_TIME_OPTIONS) as String
}

// ── Logo HTML ──────────────────────────────────────────────────

fun renderLogoHtml(
    logoUrl: String?,
    altText: String,
    cssClass: String,
    fallbackCssClass: String? = null,
): String {
    if (logoUrl != null) {
        return """<img src="$logoUrl" alt="$altText" class="$cssClass" loading="lazy" onerror="this.style.display='none'">"""
    }
    if (fallbackCssClass != null) {
        return """<span class="$fallbackCssClass">${altText.take(LOGO_FALLBACK_LENGTH).uppercase()}</span>"""
    }
    return ""
}

fun renderIconHtml(
    name: String,
    size: Int = 20,
    color: String = "currentColor",
    className: String? = null,
    extraStyle: String = "",
): String {
    val classes = buildString {
        append("fg-icon")
        if (className != null) {
            append(' ')
            append(className)
        }
    }
    return """<span class="$classes" aria-hidden="true" style="width:${size}px;height:${size}px;background-color:$color;mask-image:url('/assets/icons/$name.svg');-webkit-mask-image:url('/assets/icons/$name.svg');$extraStyle"></span>"""
}
